import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiDelete, apiPost } from "../../services/api/apiClient";

interface ProfileState {
  personalInfo: {
    firstName: string;
    middleName: string;
    lastName: string;
    position: string;
    username: string;
  };
  contactInfo: {
    email: string;
    phoneNumber: string;
    facebook: string;
    linkedin: string;
    whatsApp: string;
  };
  profileImage: string | null;
  loading: boolean;
  imageUploading: boolean;
  imageDeleting: boolean;
  passwordResetting: boolean;
  error: string | null;
  imageError: string | null;
  imageDeleteError: string | null;
  passwordError: string | null;
  updateSuccess: boolean;
  imageDeleteSuccess: boolean;
  passwordResetSuccess: boolean;
  isEditing: boolean;
  resetPasswordDialogOpen: boolean;
}

// Import existing actions
import { resetPasswordAuthorized } from "./projectsSlice";
import { updateUser } from "../../routes/dashboard/users/usersSlice";
import { updateUserData } from "./loginSlice";
import {
  updateUserPhoto,
  clearUserPhoto,
  updateUserListPhoto,
  clearUserListPhoto,
} from "../../routes/dashboard/users/usersSlice";

// Re-export existing actions for convenience
export { resetPasswordAuthorized as resetPassword };
export { updateUser as updateProfile };

// Async thunks for API calls
export const updateProfileImage = createAsyncThunk(
  "profile/updateProfileImage",
  async ({ imageFile, userId }: any, { rejectWithValue, dispatch, getState }) => {
    try {
      const formData = new FormData();
      formData.append("avatar", imageFile);

      const response = await apiPost({
        endpoint: "/upload-user-avatar",
        params: { id: userId },
        data: formData,
        withAuth: true,
      });

      // Update user photo in login slice only if it's the current user's profile
      const state = getState() as any;
      const currentUserId = state.login.user?.id;
      const selectedUserId = state.users.userId;

      if (response.imageUrl || response.avatarUrl) {
        const imageUrl = response.imageUrl || response.avatarUrl;

        // Update current user photo if it's their own profile
        if (userId === currentUserId) {
          dispatch(updateUserData({ photo: imageUrl }));
        }

        // Update selectedUser photo if admin is editing this user
        if (userId === selectedUserId) {
          dispatch(updateUserPhoto(imageUrl));
        }

        // Update the user in the users list
        dispatch(updateUserListPhoto({ userId, photoUrl: imageUrl }));
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProfileImage = createAsyncThunk(
  "profile/deleteProfileImage",
  async (userId, { rejectWithValue, dispatch, getState }) => {
    try {
      const resp = await apiDelete({
        endpoint: "/delete-user-avatar",
        params: { id: userId },
        withAuth: true,
      });

      // Update user photo in login slice only if it's the current user's profile
      const state = getState() as any;
      const currentUserId = state.login.user?.id;
      const selectedUserId = state.users.userId;

      // Update current user photo if it's their own profile
      if (userId === currentUserId) {
        dispatch(updateUserData({ photo: null }));
      }

      // Update selectedUser photo if admin is editing this user
      if (userId === selectedUserId) {
        dispatch(clearUserPhoto());
      }

      // Update the user in the users list
      dispatch(clearUserListPhoto({ userId }));

      return resp;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState: ProfileState = {
  // Profile data
  personalInfo: {
    firstName: "",
    middleName: "",
    lastName: "",
    position: "",
      username: "",
    },
    contactInfo: {
      email: "",
      phoneNumber: "",
      facebook: "",
      linkedin: "",
      whatsApp: "",
    },
    profileImage: null,

    // Loading states
    loading: false,
    imageUploading: false,
    imageDeleting: false,
    passwordResetting: false,

    // Error states
    error: null,
    imageError: null,
    imageDeleteError: null,
    passwordError: null,

    // Success states
    updateSuccess: false,
    imageDeleteSuccess: false,
    passwordResetSuccess: false, // UI state
    isEditing: false,
    resetPasswordDialogOpen: false,
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    // UI actions
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },

    setResetPasswordDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.resetPasswordDialogOpen = action.payload;
    },

    // Form data actions
    updatePersonalInfo: (state, action: PayloadAction<Partial<ProfileState["personalInfo"]>>) => {
      state.personalInfo = { ...state.personalInfo, ...action.payload };
    },

    updateContactInfo: (state, action: PayloadAction<Partial<ProfileState["contactInfo"]>>) => {
      state.contactInfo = { ...state.contactInfo, ...action.payload };
    },

    // Initialize profile from user data
    initializeFromUser: (state, action: PayloadAction<{ user: any }>) => {
      const { user } = action.payload;
      if (user && !state.personalInfo.firstName) {
        // Only initialize if not already set
        state.personalInfo = {
          ...state.personalInfo,
          username: user.username || "",
          position: user.position || user.role || "",
        };
        state.contactInfo = {
          ...state.contactInfo,
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
        };
      }
    },

    // Reset actions
    resetErrors: (state) => {
      state.error = null;
      state.imageError = null;
      state.imageDeleteError = null;
      state.passwordError = null;
    },

    resetSuccess: (state) => {
      state.updateSuccess = false;
      state.imageDeleteSuccess = false;
      state.passwordResetSuccess = false;
    },

    resetProfileState: (state) => {
      state.error = null;
      state.imageError = null;
      state.imageDeleteError = null;
      state.passwordError = null;
      state.updateSuccess = false;
      state.imageDeleteSuccess = false;
      state.passwordResetSuccess = false;
      state.isEditing = false;
      state.resetPasswordDialogOpen = false;
    },
  },
  extraReducers: (builder) => {
    // Update profile (using existing updateUser action)
    builder
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        // Update local profile state with new user data
        const user = action.payload;
        if (user) {
          state.personalInfo = {
            ...state.personalInfo,
            firstName: user.firstName || state.personalInfo.firstName,
            middleName: user.middleName || state.personalInfo.middleName,
            lastName: user.lastName || state.personalInfo.lastName,
            position: user.position || user.role || state.personalInfo.position,
            username: user.username || state.personalInfo.username,
          };
          state.contactInfo = {
            ...state.contactInfo,
            email: user.email || state.contactInfo.email,
            phoneNumber: user.phoneNumber || state.contactInfo.phoneNumber,
          };
        }
        state.updateSuccess = true;
        state.isEditing = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update profile image
    builder
      .addCase(updateProfileImage.pending, (state) => {
        state.imageUploading = true;
        state.imageError = null;
      })
      .addCase(updateProfileImage.fulfilled, (state, action: PayloadAction<any>) => {
        state.imageUploading = false;
        state.profileImage =
          action.payload.imageUrl || action.payload.avatarUrl;
        state.updateSuccess = true;
      })
      .addCase(updateProfileImage.rejected, (state, action) => {
        state.imageUploading = false;
        state.imageError = action.payload as string;
      });

    // Delete profile image
    builder
      .addCase(deleteProfileImage.pending, (state) => {
        state.imageDeleting = true;
        state.imageDeleteError = null;
      })
      .addCase(deleteProfileImage.fulfilled, (state) => {
        state.imageDeleting = false;
        state.profileImage = null;
        state.imageDeleteSuccess = true;
      })
      .addCase(deleteProfileImage.rejected, (state, action) => {
        state.imageDeleting = false;
        state.imageDeleteError = action.payload as string;
      });

    // Reset password (using existing resetPasswordAuthorized action)
    builder
      .addCase(resetPasswordAuthorized.pending, (state) => {
        state.passwordResetting = true;
        state.passwordError = null;
        state.passwordResetSuccess = false;
      })
      .addCase(resetPasswordAuthorized.fulfilled, (state) => {
        state.passwordResetting = false;
        state.passwordResetSuccess = true;
        state.resetPasswordDialogOpen = false;
      })
      .addCase(resetPasswordAuthorized.rejected, (state, action) => {
        state.passwordResetting = false;
        state.passwordError = action.payload as string;
      });
  },
});

export const {
  setEditing,
  setResetPasswordDialogOpen,
  updatePersonalInfo,
  updateContactInfo,
  initializeFromUser,
  resetErrors,
  resetSuccess,
  resetProfileState,
} = profileSlice.actions;

export default profileSlice.reducer;
